import React, { useState, useEffect } from 'react';
import { consistencySubscriptionPackages, approveSubscription, getAllSubscriptions } from '../../services/AIServices';
import { getUsers } from '../../services/userService';
import toast from 'react-hot-toast';
import { FaCrown, FaSpinner, FaCheck, FaSearch, FaUser, FaList, FaUserPlus, FaCalendarAlt, FaMoneyBillWave } from 'react-icons/fa';

const SubscriptionManagement = () => {
    const [packages, setPackages] = useState([]);
    const [users, setUsers] = useState([]);
    const [subscriptions, setSubscriptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);
    const [activeTab, setActiveTab] = useState('list'); // 'list' or 'approve'
    const [subscriptionSearch, setSubscriptionSearch] = useState('');
    
    const [formData, setFormData] = useState({
        userId: '',
        packageId: '',
        paymentReference: ''
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        await Promise.all([fetchPackages(), fetchUsers(), fetchSubscriptions()]);
        setLoading(false);
    };

    const fetchSubscriptions = async () => {
        try {
            const res = await getAllSubscriptions();
            if (res?.success) {
                setSubscriptions(res.subscriptions || []);
            }
        } catch (error) {
            console.error('Error fetching subscriptions:', error);
            toast.error('Failed to load subscriptions');
        }
    };

    const fetchPackages = async () => {
        try {
            const res = await consistencySubscriptionPackages();
            if (res?.success) {
                setPackages(res.packages || []);
                // Set first package as default
                if (res.packages?.length > 0) {
                    setFormData(prev => ({ ...prev, packageId: res.packages[0].id }));
                }
            }
        } catch (error) {
            console.error('Error fetching packages:', error);
            toast.error('Failed to load packages');
        }
    };

    const fetchUsers = async () => {
        try {
            const usersData = await getUsers();
            setUsers(usersData || []);
        } catch (error) {
            console.error('Error fetching users:', error);
            toast.error('Failed to load users');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.userId || !formData.packageId || !formData.paymentReference) {
            toast.error('All fields are required');
            return;
        }

        setSubmitting(true);
        try {
            const res = await approveSubscription(
                formData.userId,
                formData.packageId,
                formData.paymentReference
            );

            if (res?.success) {
                toast.success(res.message || 'Subscription approved successfully!');
                // Reset form and selected user
                setSelectedUser(null);
                setFormData({
                    userId: '',
                    packageId: packages[0]?.id || '',
                    paymentReference: ''
                });
                // Refresh subscriptions list
                await fetchSubscriptions();
            } else {
                toast.error(res?.message || 'Failed to approve subscription');
            }
        } catch (error) {
            console.error('Approval error:', error);
            toast.error(error?.response?.data?.message || 'Failed to approve subscription');
        } finally {
            setSubmitting(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleUserSelect = (user) => {
        setSelectedUser(user);
        setFormData(prev => ({ ...prev, userId: user.id }));
        setSearchTerm(''); // Clear search to hide dropdown
    };

    const filteredUsers = users.filter(user => {
        const search = searchTerm.toLowerCase();
        return (
            user.email?.toLowerCase().includes(search) ||
            user.username?.toLowerCase().includes(search) ||
            user.id?.toString().includes(search)
        );
    });

    const formatPrice = (price) => {
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES',
            minimumFractionDigits: 0
        }).format(price);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getTimeRemaining = (endDate) => {
        const now = new Date();
        const end = new Date(endDate);
        const diff = end - now;

        if (diff <= 0) {
            return { text: 'Expired', color: 'text-red-600 dark:text-red-400', expired: true };
        }

        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

        let timeText = '';
        let color = 'text-green-600 dark:text-green-400';

        if (days > 0) {
            timeText = `${days}d ${hours}h remaining`;
            if (days <= 1) color = 'text-orange-600 dark:text-orange-400';
        } else if (hours > 0) {
            timeText = `${hours}h ${minutes}m remaining`;
            color = 'text-orange-600 dark:text-orange-400';
        } else {
            timeText = `${minutes}m remaining`;
            color = 'text-red-600 dark:text-red-400';
        }

        return { text: timeText, color, expired: false };
    };

    const filteredAndSortedSubscriptions = subscriptions
        .filter(sub => {
            const search = subscriptionSearch.toLowerCase();
            return (
                sub.user_name?.toLowerCase().includes(search) ||
                sub.user_email?.toLowerCase().includes(search) ||
                sub.package_name?.toLowerCase().includes(search) ||
                sub.payment_reference?.toLowerCase().includes(search) ||
                sub.status?.toLowerCase().includes(search)
            );
        })
        .sort((a, b) => {
            // Sort by status: active first, then expired
            if (a.status === 'active' && b.status !== 'active') return -1;
            if (a.status !== 'active' && b.status === 'active') return 1;
            
            // Within same status, sort by end_date (soonest expiring first for active)
            if (a.status === 'active' && b.status === 'active') {
                return new Date(a.end_date) - new Date(b.end_date);
            }
            
            // For expired, sort by most recently expired first
            return new Date(b.end_date) - new Date(a.end_date);
        });

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <FaSpinner className="animate-spin text-4xl text-blue-600" />
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 border border-gray-200 dark:border-gray-700">
                        {/* Header */}
                        <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200 dark:border-gray-700">
                            <div className="p-3 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-xl">
                                <FaCrown className="text-2xl text-white" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Premium Subscriptions</h2>
                                <p className="text-sm text-gray-600 dark:text-gray-400">Manage and approve user subscriptions</p>
                            </div>
                        </div>

                        {/* Tabs */}
                        <div className="flex gap-2 mb-6">
                            <button
                                onClick={() => setActiveTab('list')}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                                    activeTab === 'list'
                                        ? 'bg-blue-600 text-white shadow-md'
                                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                }`}
                            >
                                <FaList />
                                <span>All Subscriptions</span>
                                <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs">{subscriptions.length}</span>
                            </button>
                            <button
                                onClick={() => setActiveTab('approve')}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                                    activeTab === 'approve'
                                        ? 'bg-green-600 text-white shadow-md'
                                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                }`}
                            >
                                <FaUserPlus />
                                <span>Approve New</span>
                            </button>
                        </div>

                        {/* Approve New Subscription Form */}
                        {activeTab === 'approve' && (
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* User Search & Selection */}
                            {!selectedUser && (
                                <div className="relative">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Search & Select User <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 z-10" />
                                        <input
                                            type="text"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            placeholder="Type to search by email, username or ID..."
                                            className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                                        />
                                    </div>

                                    {/* Dropdown Results */}
                                    {searchTerm && (
                                    <div className="absolute z-20 w-full mt-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-xl max-h-80 overflow-y-auto">
                                        {filteredUsers.length === 0 ? (
                                            <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
                                                No users found matching "{searchTerm}"
                                            </div>
                                        ) : (
                                            <div className="p-2">
                                                {filteredUsers.slice(0, 50).map(user => (
                                                    <button
                                                        key={user.id}
                                                        type="button"
                                                        onClick={() => handleUserSelect(user)}
                                                        className="w-full text-left p-3 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors"
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold">
                                                                {user.email?.[0]?.toUpperCase() || 'U'}
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                                                    {user.email || user.username || 'Unknown'}
                                                                </p>
                                                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                                                    ID: {user.id} • {user.role || 'user'}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </button>
                                                ))}
                                                {filteredUsers.length > 50 && (
                                                    <div className="p-3 text-xs text-center text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700">
                                                        Showing first 50 results. Keep typing to narrow down...
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    )}
                                </div>
                            )}

                            {/* Selected User Info */}
                            {selectedUser && (
                                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl p-4 border border-blue-200 dark:border-blue-800">
                                    <div className="flex items-start justify-between">
                                        <div>
                                            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                                                <FaCheck className="text-green-600" />
                                                Selected User
                                            </h3>
                                            <div className="space-y-1 text-sm text-gray-700 dark:text-gray-300">
                                                <p><strong>Email:</strong> {selectedUser.email || 'N/A'}</p>
                                                <p><strong>Username:</strong> {selectedUser.username || 'N/A'}</p>
                                                <p><strong>User ID:</strong> {selectedUser.id}</p>
                                                <p><strong>Role:</strong> {selectedUser.role || 'user'}</p>
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setSelectedUser(null);
                                                setFormData(prev => ({ ...prev, userId: '' }));
                                            }}
                                            className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 text-sm font-medium"
                                        >
                                            Change
                                        </button>
                                    </div>
                                </div>
                            )}

                    {/* Package Selection */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Subscription Package <span className="text-red-500">*</span>
                        </label>
                        <select
                            name="packageId"
                            value={formData.packageId}
                            onChange={handleChange}
                            className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                            required
                        >
                            {packages.map(pkg => (
                                <option key={pkg.id} value={pkg.id}>
                                    {pkg.name} - {formatPrice(pkg.price)} ({pkg.duration_days} {pkg.duration_days === 1 ? 'day' : 'days'})
                                </option>
                            ))}
                        </select>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            Select the package the user paid for
                        </p>
                    </div>

                    {/* Payment Reference */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            M-Pesa Reference Code <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            name="paymentReference"
                            value={formData.paymentReference}
                            onChange={handleChange}
                            placeholder="e.g., SH12345678"
                            className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                            required
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            The M-Pesa transaction reference code provided by the user
                        </p>
                    </div>

                    {/* Selected Package Info */}
                    {formData.packageId && (
                        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl p-4 border border-blue-200 dark:border-blue-800">
                            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Selected Package Details:</h3>
                            {packages.find(p => p.id === parseInt(formData.packageId)) && (
                                <div className="space-y-1 text-sm text-gray-700 dark:text-gray-300">
                                    <p><strong>Package:</strong> {packages.find(p => p.id === parseInt(formData.packageId)).name}</p>
                                    <p><strong>Price:</strong> {formatPrice(packages.find(p => p.id === parseInt(formData.packageId)).price)}</p>
                                    <p><strong>Duration:</strong> {packages.find(p => p.id === parseInt(formData.packageId)).duration_days} days</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Submit Button */}
                    <button
                        type="submit"
                        disabled={submitting || !selectedUser}
                        className="w-full py-3 px-6 rounded-xl font-semibold text-white bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:scale-105 active:scale-95 shadow-lg flex items-center justify-center gap-2"
                    >
                        {submitting ? (
                            <>
                                <FaSpinner className="animate-spin" />
                                <span>Approving...</span>
                            </>
                        ) : !selectedUser ? (
                            <>
                                <FaUser />
                                <span>Select a User First</span>
                            </>
                        ) : (
                            <>
                                <FaCheck />
                                <span>Approve Subscription</span>
                            </>
                        )}
                    </button>

                    {/* Instructions */}
                    <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">📋 Quick Guide:</h3>
                        <ol className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                            <li className="flex items-start gap-2">
                                <span className="text-blue-600 dark:text-blue-400 font-bold">1.</span>
                                <span>User pays via M-Pesa and contacts you</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-blue-600 dark:text-blue-400 font-bold">2.</span>
                                <span>Verify payment in your M-Pesa</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-blue-600 dark:text-blue-400 font-bold">3.</span>
                                <span>Type user's email/username in search box above</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-blue-600 dark:text-blue-400 font-bold">4.</span>
                                <span>Click user from dropdown, select package, enter M-Pesa code</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-blue-600 dark:text-blue-400 font-bold">5.</span>
                                <span>Click "Approve" - User gets instant Premium access! ✨</span>
                            </li>
                        </ol>
                    </div>
                </form>
                        )}

                        {/* All Subscriptions List */}
                        {activeTab === 'list' && (
                            <div className="space-y-4">
                                {/* Stats Summary */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                                        <div className="text-sm text-green-600 dark:text-green-400 font-medium">Active Subscriptions</div>
                                        <div className="text-2xl font-bold text-green-900 dark:text-green-300">
                                            {subscriptions.filter(s => s.status === 'active').length}
                                        </div>
                                    </div>
                                    <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4">
                                        <div className="text-sm text-red-600 dark:text-red-400 font-medium">Expired Subscriptions</div>
                                        <div className="text-2xl font-bold text-red-900 dark:text-red-300">
                                            {subscriptions.filter(s => s.status === 'expired').length}
                                        </div>
                                    </div>
                                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                                        <div className="text-sm text-blue-600 dark:text-blue-400 font-medium">Total Revenue</div>
                                        <div className="text-2xl font-bold text-blue-900 dark:text-blue-300">
                                            {formatPrice(subscriptions.reduce((sum, s) => sum + parseFloat(s.amount_paid || 0), 0))}
                                        </div>
                                    </div>
                                </div>

                                {/* Search Bar */}
                                <div className="relative">
                                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 z-10" />
                                    <input
                                        type="text"
                                        value={subscriptionSearch}
                                        onChange={(e) => setSubscriptionSearch(e.target.value)}
                                        placeholder="Search by user name, email, package, reference, or status..."
                                        className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                                    />
                                </div>

                                {/* Subscriptions Table */}
                                {filteredAndSortedSubscriptions.length === 0 ? (
                                    <div className="text-center py-12">
                                        <FaCrown className="mx-auto text-6xl text-gray-300 dark:text-gray-600 mb-4" />
                                        <p className="text-gray-500 dark:text-gray-400">
                                            {subscriptionSearch ? 'No subscriptions found matching your search' : 'No subscriptions yet'}
                                        </p>
                                    </div>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                            <thead className="bg-gray-50 dark:bg-gray-700">
                                                <tr>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">User</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Package</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Duration</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Amount</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Payment Ref</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Time Remaining</th>
                                                    {/*<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Start Date</th>*/}
                                                    {/*<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">End Date</th>*/}
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Approved By</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                                {filteredAndSortedSubscriptions.map((sub) => {
                                                    const timeRemaining = getTimeRemaining(sub.end_date);
                                                    return (
                                                    <tr key={sub.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition">
                                                        <td className="px-4 py-4">
                                                            <div className="flex items-center">
                                                                <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold">
                                                                    {sub.user_name?.[0]?.toUpperCase() || 'U'}
                                                                </div>
                                                                <div className="ml-3">
                                                                    <div className="text-sm font-medium text-gray-900 dark:text-white">{sub.user_name}</div>
                                                                    <div className="text-xs text-gray-500 dark:text-gray-400">{sub.user_email}</div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-4">
                                                            <div className="text-sm text-gray-900 dark:text-white font-medium">{sub.package_name}</div>
                                                            <div className="text-xs text-gray-500 dark:text-gray-400">{formatPrice(sub.package_price)}</div>
                                                        </td>
                                                        <td className="px-4 py-4 whitespace-nowrap">
                                                            <div className="flex items-center gap-1 text-sm text-gray-700 dark:text-gray-300">
                                                                <FaCalendarAlt className="text-gray-400" />
                                                                <span>{sub.duration_days} {sub.duration_days === 1 ? 'day' : 'days'}</span>
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-4 whitespace-nowrap">
                                                            <div className="flex items-center gap-1 text-sm font-medium text-green-600 dark:text-green-400">
                                                                <FaMoneyBillWave />
                                                                <span>{formatPrice(sub.amount_paid)}</span>
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-4 whitespace-nowrap">
                                                            <span className="font-mono text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-gray-700 dark:text-gray-300">
                                                                {sub.payment_reference}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-4 whitespace-nowrap">
                                                            <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                                                sub.status === 'active'
                                                                    ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400'
                                                                    : sub.status === 'expired'
                                                                    ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400'
                                                                    : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400'
                                                            }`}>
                                                                {sub.status}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-4 whitespace-nowrap">
                                                            <div className={`text-sm font-semibold ${timeRemaining.color}`}>
                                                                {timeRemaining.text}
                                                            </div>
                                                        </td>
                                                        {/*<td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">*/}
                                                        {/*    {formatDate(sub.start_date)}*/}
                                                        {/*</td>*/}
                                                        {/*<td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">*/}
                                                        {/*    {formatDate(sub.end_date)}*/}
                                                        {/*</td>*/}
                                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                                                            {sub.approved_by_name || 'N/A'}
                                                        </td>
                                                    </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        )}
            </div>
        </div>
    );
};

export default SubscriptionManagement;
